import React, { useState } from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AlertTriangle,
  ClipboardCheck,
  FileText,
  Info,
  Plus,
  Trash2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import ListItemCardCst from "@components/ListItemCardCst";
import InputCst from "@components/InputCst/InputCst";
import SelectCst from "@components/InputCst/SelectCst";
import AddTestSurveyModalCst, {
  AddTestSurveySelectedItem,
} from "@components/AddTestSurveyModalCst";
import { WizardStepProps } from "@components/WizardCst/types";
import { AssociationItem } from "../../AddActivity/types";
import SelectedLOList from "@components/SelectedLOList";
import { TEST_LOGIC_OPTIONS } from "../config/courseOptions";
import { CourseWizardData, TestLogic } from "../types";

const SectionDivider: React.FC = () => <Divider sx={{ opacity: 0.6 }} />;

const SectionHeader: React.FC<{ title: string }> = ({ title }) => {
  const theme = useTheme();
  return (
    <Typography
      sx={{
        fontSize: "18px",
        fontWeight: 700,
        color: theme.palette.text.primary,
      }}
    >
      {title}
    </Typography>
  );
};

interface TestsSurveyStepProps extends WizardStepProps<CourseWizardData> {
  lockTestSurvey?: boolean;
}

const TestsSurveyStep: React.FC<TestsSurveyStepProps> = ({
  data,
  setData,
  errors,
  lockTestSurvey = false,
}) => {
  const { t } = useTranslation("wizard-course");
  const { t: tCatalog } = useTranslation("catalog-v2");
  const theme = useTheme();

  const [pickerOpen, setPickerOpen] = useState(false);

  const activities = data.selected_activities;
  const noActivities = activities.length === 0;
  const hasTest = !!data.test_id;
  const hasSurvey = !!data.survey_id;
  const showLogicSection = hasTest;
  const showMaxAttempt = data.test_logic === "WITH_ZEROING";

  const errMsg = (key: string): string =>
    errors[key] ? t(`errors.${errors[key]}`) || "" : "";

  const handlePickerConfirm = (selection: AddTestSurveySelectedItem | null) => {
    if (!selection) return;
    if (selection.kind === "test") {
      setData({ test_id: { id: selection.id, label: selection.label } });
    } else {
      setData({ survey_id: { id: selection.id, label: selection.label } });
    }
  };

  const handleRemoveTest = () => {
    setData({
      test_id: null,
      test_logic: null,
      test_min_score: "",
      max_attempt: "",
    });
  };

  const handleRemoveSurvey = () => {
    setData({ survey_id: null });
  };

  const handleRemoveActivity = (id: number) => {
    setData({ selected_activities: activities.filter((a) => a.id !== id) });
  };

  // TODO: pagina dettaglio v2 (attività/test/survey) non disponibile.
  const handleDetailsActivity = (id: number) => {
    console.info("[course-wizard] activity details — TODO", id);
  };
  const handleDetailsTest = () => {
    console.info("[course-wizard] test details — TODO", data.test_id?.id);
  };
  const handleDetailsSurvey = () => {
    console.info("[course-wizard] survey details — TODO", data.survey_id?.id);
  };

  // TODO: integrare la creazione di un nuovo test/survey.
  const handleCreateNew = (tabKey?: string) => {
    console.info("[course-wizard] crea nuovo test o survey — TODO", tabKey);
  };

  const renderAssociationRow = (
    item: AssociationItem,
    kind: "test" | "survey",
    onDetails: () => void,
    onRemove: () => void,
  ) => (
    <ListItemCardCst
      icon={
        kind === "test" ? <ClipboardCheck size={20} /> : <FileText size={20} />
      }
      title={item.label || `#${item.id}`}
      subtitle={{
        id: item.id,
        activityType: kind === "test" ? "TEST" : "SURVEY",
      }}
      actions={[
        // {
        //   label: t("step-tests-survey.details") || "Dettagli",
        //   onClick: onDetails,
        // },
        {
          label: t("step-tests-survey.remove") || "Rimuovi",
          icon: <Trash2 size={18} />,
          iconOnly: true,
          disabled: lockTestSurvey,
          onClick: () => {
            if (lockTestSurvey) return;
            onRemove();
          },
        },
      ]}
    />
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {noActivities && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            padding: "12px 16px",
            borderRadius: "12px",
            backgroundColor: theme.palette.warning.light,
            border: `1px solid ${theme.palette.warning.main}`,
          }}
        >
          <AlertTriangle size={20} color={theme.palette.warning.dark} />
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              color: theme.palette.common.black,
            }}
          >
            {t("step-activities.empty-warning")}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          padding: "12px 16px",
          borderRadius: "12px",
          backgroundColor: theme.palette.error.light,
          border: `1px solid ${theme.palette.error.main}`,
        }}
      >
        <Info size={20} color={theme.palette.error.dark} />
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 500,
            color: theme.palette.common.black,
          }}
        >
          {t("step-tests-survey.banner")}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Button
          onClick={() => setPickerOpen(true)}
          variant="contained"
          startIcon={<Plus size={16} />}
          disabled={(hasTest && hasSurvey) || lockTestSurvey}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": { backgroundColor: theme.palette.primary.dark },
          }}
        >
          {t("step-tests-survey.add")}
        </Button>
        {/*
        <Button
          onClick={() => handleCreateNew()}
          variant="outlined"
          startIcon={<Pencil size={16} />}
          disabled={lockTestSurvey}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary,
          }}
        >
          {t("step-tests-survey.create-new")}
        </Button>
        */}
        <Box sx={{ flex: 1 }} />
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          {[
            t("step-tests-survey.counter.activities", {
              count: activities.length,
            }),
            hasTest && t("step-tests-survey.counter.test", { count: 1 }),
            hasSurvey && t("step-tests-survey.counter.survey", { count: 1 }),
          ]
            .filter(Boolean)
            .join(" · ")}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <SectionHeader title={t("step-tests-survey.sections.activities")} />
        {activities.length === 0 ? (
          <Typography
            sx={{
              fontSize: "13px",
              color: theme.palette.text.primary,
            }}
          >
            {t("step-tests-survey.activities-empty")}
          </Typography>
        ) : (
          <SelectedLOList
            items={activities}
            tCatalog={tCatalog}
            removeLabel={t("step-tests-survey.remove")}
            onRemove={handleRemoveActivity}
            onDetails={(item) => handleDetailsActivity(item.id)}
          />
        )}
      </Box>

      {(hasTest || hasSurvey) && (
        <>
          <SectionDivider />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <SectionHeader
              title={t("step-tests-survey.sections.tests-survey")}
            />
            {hasTest &&
              data.test_id &&
              renderAssociationRow(
                data.test_id,
                "test",
                handleDetailsTest,
                handleRemoveTest,
              )}
            {hasSurvey &&
              data.survey_id &&
              renderAssociationRow(
                data.survey_id,
                "survey",
                handleDetailsSurvey,
                handleRemoveSurvey,
              )}
          </Box>
        </>
      )}

      {showLogicSection && (
        <>
          <SectionDivider />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                }}
              >
                {t("step-tests-survey.logic.title")}
              </Typography>
              <Typography
                sx={{
                  fontSize: "13px",
                  color: theme.palette.text.primary,
                  lineHeight: 1.5,
                  mt: 0.5,
                }}
              >
                {t("step-tests-survey.logic.description")}
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SelectCst
                  id="course-test-logic"
                  label={`${t("step-tests-survey.logic.field-logic")} *`}
                  value={data.test_logic || ""}
                  onChange={(v: any) => {
                    const next = (v || null) as TestLogic | null;
                    setData({
                      test_logic: next,
                      max_attempt:
                        next === "WITH_ZEROING" ? data.max_attempt : "",
                    });
                  }}
                  options={TEST_LOGIC_OPTIONS.map((o) => ({
                    value: o.value,
                    label: t(o.i18nKey) || o.value,
                  }))}
                  error={!!errors.test_logic}
                  errorMessage={errMsg("test_logic")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InputCst
                  id="course-test-min-score"
                  label={t("step-tests-survey.logic.field-min-score") || ""}
                  value={data.test_min_score}
                  onChange={(e: any) =>
                    setData({ test_min_score: e.target.value })
                  }
                  type="number"
                  min={0}
                  disabled={lockTestSurvey}
                />
              </Grid>
              {showMaxAttempt && (
                <Grid item xs={12} md={6}>
                  <InputCst
                    id="course-max-attempt"
                    label={`${t("step-tests-survey.logic.field-max-attempt")} *`}
                    value={data.max_attempt}
                    onChange={(e: any) =>
                      setData({ max_attempt: e.target.value })
                    }
                    type="number"
                    min={1}
                    error={!!errors.max_attempt}
                    errorMessage={errMsg("max_attempt")}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        </>
      )}

      <AddTestSurveyModalCst
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={handlePickerConfirm}
        onCreate={(tabKey) => handleCreateNew(tabKey)}
        initialTab={hasTest ? "survey" : "test"}
      />
    </Box>
  );
};

export default TestsSurveyStep;
